import {createContact, editContact} from "../../api";
import {Modal} from "react-bootstrap-v5";
import React, {useState} from "react";
import {KTSVG} from "../../../../../_metronic/helpers";
import {ExtendedModalProps} from "../../models/Modal";
import {SimCardContact} from "../../models/Models";
import clsx from "clsx";
import * as Yup from "yup";
import {useFormik} from "formik";
import InputMask from "react-input-mask";
import Alert from "../../../../components/alerts/Alert";

interface IContactForm extends ExtendedModalProps {
    contact?: SimCardContact | undefined
}

const schema = Yup.object().shape({
    name: Yup.string()
        .required('Обязательное поле'),
    phone: Yup.string()
        .matches(/^(?:.*?\d){11}$/, {
            message: 'Обязательное поле из 11 цифр',
            excludeEmptyStrings: true
        })
        .required('Обязательное поле'),
    email: Yup.string()
        .matches(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, {
            message: 'Введите корректный E-mail или оставте поле пустым.',
        })
})

const initialValues = {
    name: '',
    phone: '',
    email: '',
    descr: ''
} as Partial<SimCardContact>

/**
 * Форма создания контакта
 */
export const ContactForm = ({contact, show, handleClose}: IContactForm) => {
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState('')

    /**
     * Сброс формы при открытии модального окна
     */
    const reset = () => {
        setMessage('')
        formik.resetForm()
    }

    const formik = useFormik({
        initialValues: contact || initialValues,
        enableReinitialize: true,
        validationSchema: schema,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setLoading(true)
            const response = (contact)
                ? await editContact({...values, id: contact.id})
                : await createContact(values)
            switch (response.status) {
                case "ok":
                    setLoading(false)
                    handleClose({isSubmitted: true})
                    return true
                default:
                    setLoading(false)
                    setSubmitting(false)
                    console.log(response)
                    setMessage("Сервер не смог обработать запрос. Попробуйте выполнить операцию позднее.")
                    return false
            }
        },
    })

    /**
     * Предотвращение лишних запросов при обычном закрытии окна
     */
    const optimizedModalCloser = () => {
        handleClose({isSubmitted: false})
    }

    return (
        <Modal
            className='modal'
            role='dialog'
            aria-hidden='true'
            tabIndex='-1'
            show={show}
            dialogClassName="modal-dialog modal-dialog-scrollable w-350px"
            onShow={reset}
            onHide={optimizedModalCloser}
        >
            <div className='modal-header py-4'>
                <h3>{contact ? 'Изменение' : 'Создание'} контакта</h3>

                <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={optimizedModalCloser}>
                    <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1'/>
                </div>
            </div>

            <div className='modal-body pt-5 px-5 center'>

                {message !== "" && <Alert additionalClassNames='p-5 mb-5 mt-0' message={message}/>}

                <form
                    className='form w-100'
                    onSubmit={formik.handleSubmit}
                    noValidate
                >
                    <div className='fv-row mb-5'>
                        <label className='form-label fs-6 fw-bolder text-dark px-1'>Наименование контакта</label>
                        <input
                            type='text'
                            placeholder=''
                            autoComplete='off'
                            {...formik.getFieldProps('name')}
                            className={clsx(
                                'form-control form-control-lg form-control-solid',
                                {
                                    'is-invalid': formik.touched.name && formik.errors.name
                                },
                                {
                                    'is-valid': formik.touched.name && !formik.errors.name,
                                }
                            )}
                        />
                        {formik.touched.name && formik.errors.name && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block error-color'>
                                    <span role='alert'>{formik.errors.name}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='fv-row mb-5'>
                        <label className='form-label fs-6 fw-bolder text-dark px-1'>Телефон</label>
                        <InputMask
                            mask={"+7 (999) 999-99-99"}
                            autoComplete='off'
                            {...formik.getFieldProps('phone')}
                            className={clsx(
                                'form-control form-control-lg form-control-solid',
                                {
                                    'is-invalid': formik.touched.phone && formik.errors.phone,
                                },
                                {
                                    'is-valid': formik.touched.phone && !formik.errors.phone,
                                }
                            )}
                        />
                        {formik.touched.phone && formik.errors.phone && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block error-color'>
                                    <span role='alert'>{formik.errors.phone}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='fv-row mb-5'>
                        <label className='form-label fs-6 fw-bolder text-dark px-1'>E-mail</label>
                        <input
                            type='text'
                            autoComplete='off'
                            {...formik.getFieldProps('email')}
                            className={clsx(
                                'form-control form-control-lg form-control-solid',
                                {
                                    'is-invalid': formik.touched.email && formik.errors.email,
                                },
                                {
                                    'is-valid': formik.touched.email && !formik.errors.email,
                                }
                            )}
                        />
                        {formik.touched.email && formik.errors.email && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block error-color'>
                                    <span role='alert'>{formik.errors.email}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='fv-row mb-5'>
                        <label className='form-label fs-6 fw-bolder text-dark px-1'>Описание</label>
                        <input
                            type='text'
                            autoComplete='off'
                            {...formik.getFieldProps('descr')}
                            className={clsx(
                                'form-control form-control-lg form-control-solid',
                                {
                                    'is-invalid': formik.touched.descr && formik.errors.descr,
                                },
                                {
                                    'is-valid': formik.touched.descr && !formik.errors.descr,
                                }
                            )}
                        />
                        {formik.touched.descr && formik.errors.descr && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block error-color'>
                                    <span role='alert'>{formik.errors.descr}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='text-center d-flex justify-content-end'>
                        <button
                            type='submit'
                            className='btn btn-primary'
                            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                        >
                            {!loading && <span className='indicator-label'>Сохранить</span>}
                            {loading && (
                                <span className='indicator-progress' style={{display: 'block'}}>Обрабатываем...
                            <span className='spinner-border spinner-border-sm align-middle ms-2'> </span>
                        </span>
                            )}
                        </button>

                    </div>
                </form>
            </div>
        </Modal>
    )
}
